import React from 'react';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../lib/reportRoleColors';

export interface ReportSourceNoteProps {
  children: React.ReactNode;
  /** Compact uppercase treatment for a short `Source: …` label. */
  variant?: 'body' | 'label';
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  /** Keep a short source note intact at a page seam. Leave false for long lists. */
  keepTogether?: boolean;
}

/**
 * Shared source-attribution typography for report bodies.
 *
 * The semantic marker is consumed by the common print/deck density layer,
 * which pins source notes to 8pt even when the report-wide paragraph floor is
 * 10.5pt. The continuous report remains a compact 10px. Long source lists stay
 * fragmentable unless the caller explicitly opts into `keepTogether`.
 */
export function ReportSourceNote({
  children,
  variant = 'body',
  className = 'm-0',
  style,
  testId,
  keepTogether = false,
}: ReportSourceNoteProps): React.ReactElement {
  const theme = useEffectiveReportTheme();
  const variantClass = variant === 'label'
    ? 'font-semibold uppercase tracking-[0.1em]'
    : 'font-normal tracking-normal';

  return (
    <p
      className={`text-[10px] leading-relaxed ${variantClass} ${className}`.trim()}
      style={{
        color: roleColor(theme, 'muted'),
        fontFamily: 'var(--report-body-font, inherit)',
        ...style,
      }}
      data-report-source-note="true"
      data-layout-group={keepTogether ? 'row' : undefined}
      data-testid={testId}
    >
      {children}
    </p>
  );
}
